import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { FileUpload, Info } from "@vedmarket/ui-kit";

import {
  useCreateSupportTicketMutation,
  useGetSupportStepsQuery,
} from "../../../../../../../api/support";
import {getCalcId, useGetOrdersMutation} from "../../../../../../../api/calc";
import AlertBottom from "../../../../../../common/alertBottom";
import Preloader from "../../../../../../common/preloader";
import {COLORS} from "../../../../../../../config/constants";
import {
  SUPPORT_ATTACHMENT_ACCEPTED_TYPES,
  SUPPORT_ATTACHMENT_MAX_SIZE,
  SUPPORT_TICKET_CREATE_CACHE_KEY,
} from "../../../../support-constants";
import {useSupportSteps} from "../../../../support-hooks";

import './support-request-form.scss'

const SUPPORT_CATEGORY = 'calculator';

type Props = {
  formId: string
}

type AlertState = {
  isError: boolean
  message: string
  open: boolean
}

const SupportRequestForm = ({formId}: Props) => {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const steps = useSupportSteps();
  const {isLoading} = useGetSupportStepsQuery({category: SUPPORT_CATEGORY});
  const [ createSupportTicket, {isLoading: isSubmitting} ] = useCreateSupportTicketMutation({
    fixedCacheKey: SUPPORT_TICKET_CREATE_CACHE_KEY,
  });
  const [ getOrders, {isLoading: isLoadingCalculations} ] = useGetOrdersMutation();

  const [ selectedStep, setSelectedStep ] = useState('');
  const [ selectedCalculation, setSelectedCalculation ] = useState('');

  const [ calculations, setCalculations ] = useState<Calculation[]>([]);

  const [ subject, setSubject ] = useState('');
  const [ message, setMessage ] = useState('');

  const [ attachments, setAttachments ] = useState<File[]>([]);

  const [ alert, setAlert ] = useState<AlertState>({
    isError: false,
    message: '',
    open: false,
  });

  useEffect(() => {
    if (!selectedStep && steps.length) {
      setSelectedStep(steps[0].value);
    }
  }, [ selectedStep, steps ]);

  useEffect(() => {
    getOrders({ordering: '-created_at'}).unwrap().then(({results}) => {
      const lastEditedCalculationId = getCalcId();
      const lastEditedCalculation = results.find(({num}) => num === lastEditedCalculationId);
      const defaultCalculation = lastEditedCalculation || results[0];

      setCalculations(results);
      setSelectedCalculation(defaultCalculation?.num || '');
    }).catch(() => {
      setCalculations([]);
    });
  }, [ getOrders ]);

  const handleFile = (file: File) => {
    if (file.size > SUPPORT_ATTACHMENT_MAX_SIZE) {
      setAlert({
        isError: true,
        message: t('support__message__attachmentTooLarge'),
        open: true,
      });

      return false;
    }

    setAttachments((files) => [ ...files, file ]);

    return true;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedStep || isSubmitting) {
      return;
    }

    try {
      await createSupportTicket({
        attachments,
        ...(selectedCalculation && {calculation_number: selectedCalculation}),
        category: SUPPORT_CATEGORY,
        message,
        step: selectedStep,
        subject,
      }).unwrap();

      navigate('/cabinet/support', {
        state: {
          requestCreated: true,
        } as SupportRequestLocationState,
      });
    } catch {
      setAlert({
        isError: true,
        message: t('support__message__requestCreateFailed'),
        open: true,
      });
    }
  }

  const handleCloseAlert = () => {
    setAlert((value) => ({...value, open: false}));
  }

  const handleRemoveFile = (index: number) => {
    setAttachments((files) => files.filter((file, fileIndex) => fileIndex !== index));
  }

  return (
    <form id={formId} className='support-request-form' onSubmit={handleSubmit}>
      <TextField
        select
        fullWidth
        label={t('support__text__requestCategory')}
        value={SUPPORT_CATEGORY}
        InputLabelProps={{shrink: true}}
      >
        <MenuItem value={SUPPORT_CATEGORY}>{t('support__text__requestCategoryCalculator')}</MenuItem>
      </TextField>
      <FormControl className='support-request-form__steps'>
        <Typography variant='subtitle1' sx={{color: COLORS.BLACK_DARK, fontWeight: 500, lineHeight: '24px', paddingBottom: '8px'}}>
          {t('support__text__requestSteps')}
        </Typography>
        {isLoading ? (
          <Preloader />
        ) : (
          <RadioGroup value={selectedStep} onChange={(event) => setSelectedStep(event.target.value)}>
            {steps.map(({label, value}) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={label}
                sx={{margin: 0, '.MuiRadio-root': {padding: '8px'}, '.MuiFormControlLabel-label': {lineHeight: '28px'}}}
              />
            ))}
          </RadioGroup>
        )}
      </FormControl>
      <TextField
        select
        fullWidth
        disabled={isLoadingCalculations || !calculations.length}
        label={t('support__text__requestCalculation')}
        value={selectedCalculation}
        onChange={(event) => setSelectedCalculation(event.target.value)}
        InputLabelProps={{shrink: true}}
      >
        {calculations.map((calculation) => (
          <MenuItem key={calculation.num} value={calculation.num}>
            № {calculation.num} {t('common__text__from')} {moment(calculation.created_at).format('DD.MM.YYYY HH:mm:ss')}
          </MenuItem>
        ))}
      </TextField>
      <Alert
        icon={<Info />}
        severity='info'
        sx={{
          '&.MuiAlert-standardInfo': {
            backgroundColor: COLORS.INFO_ALERT_BACKGROUND,
            color: COLORS.WHITE,
            svg: {
              fill: COLORS.BLUE_DARK,
            }
          },
          padding: '16px',
          '.MuiAlert-message': {
            padding: 0,
          },
          '.MuiAlert-icon': {
            padding: 0,
          }
        }}
      >
        <Typography variant='body2' sx={{color: COLORS.INFO_ALERT_TEXT, letterSpacing: 0, lineHeight: '22px'}}>
          {t('support__text__requestTechnicalHint')}
          .
        </Typography>
      </Alert>
      <TextField
        required
        fullWidth
        label={t('support__text__requestSubject')}
        value={subject}
        onChange={(event) => setSubject(event.target.value)}
        InputLabelProps={{shrink: true}}
      />
      <TextField
        required
        fullWidth
        multiline
        label={t('support__text__requestMessage')}
        minRows={5}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        InputLabelProps={{shrink: true}}
        sx={{'.MuiInputBase-root': {height: '160px', alignItems: 'flex-start'}}}
      />
      {attachments.map((file, index) => (
        <FileUpload
          key={`${file.name}-${file.lastModified}-${index}`}
          acceptedFilesConfig={SUPPORT_ATTACHMENT_ACCEPTED_TYPES}
          initialFileTitle={file.name}
          onRemove={() => handleRemoveFile(index)}
        />
      ))}
      <FileUpload
        key={`support-attachment-${attachments.length}`}
        acceptedFilesConfig={SUPPORT_ATTACHMENT_ACCEPTED_TYPES}
        onHandleFile={handleFile}
      />
      <AlertBottom
        open={alert.open}
        isError={alert.isError}
        message={alert.message}
        onClose={handleCloseAlert}
      />
    </form>
  );
};

export default SupportRequestForm;
